/**
 * Las sustituciones manuales vigentes.
 *
 * Son **estado acumulado**, no un efecto de cada arrastre: se mandan todas en
 * cada replanificación, porque si cada arrastre mandara solo lo suyo el anterior
 * se perdería y las paradas saltarían de día solas.
 *
 * Módulo puro: sin estado ni HTTP. El drawer no se puede montar en Karma
 * (`ave-models-package` arrastra `swagger-ui-dist`), así que esta lógica vive
 * afuera para poder probarse.
 */

/** Un cambio manual: entra un cliente sin día, sale el que ocupaba ese lugar. */
export interface Substitution {
  inId: string;
  inName: string;
  /** Cliente desplazado, o `null` si el día no tenía visitas. */
  outId: string | null;
  outName: string;
  dayCode: number;
  date: string;
  dayLabel: string;
}

/**
 * Registra una sustitución.
 *
 * Un cliente se visita una sola vez por semana, así que si ya entraba se
 * reubica en vez de duplicarse. Y una parada no puede salir dos veces: la
 * sustitución nueva sobre el mismo desplazado reemplaza a la anterior.
 */
export function addSubstitution(
  current: readonly Substitution[],
  change: Substitution,
): Substitution[] {
  return [
    ...current.filter(
      (sub) => sub.inId !== change.inId && !(change.outId && sub.outId === change.outId),
    ),
    change,
  ];
}

/** Deshace un cambio: sale de las vigentes y con él vuelve su desplazado. */
export function removeSubstitution(
  current: readonly Substitution[],
  inId: string,
): Substitution[] {
  return current.filter((sub) => sub.inId !== inId);
}

/** Qué día le toca a cada cliente que entra. */
export function pinnedOf(current: readonly Substitution[]): ReadonlyMap<string, number> {
  return new Map(current.map((sub) => [sub.inId, sub.dayCode]));
}

/** De qué día sale cada desplazado. */
export function displacedOf(current: readonly Substitution[]): ReadonlyMap<string, number> {
  return new Map(
    current.filter((sub) => sub.outId).map((sub) => [sub.outId as string, sub.dayCode]),
  );
}

/** Los clientes que salieron por una sustitución. */
export function displacedIds(current: readonly Substitution[]): string[] {
  return current.filter((sub) => sub.outId).map((sub) => sub.outId as string);
}
